import { useEffect, useState, useMemo } from 'react';
import { X, BarChart3, AlertCircle } from 'lucide-react';
import { votesApi } from '../../api/votes';
import { subsetsApi } from '../../api/subsets';
import { songsApi } from '../../api/songs';
import type { VoteTally, SubsetWithSongs, SongSummary } from '../../types';

interface TallyModalProps {
  subsetId: number;
  onClose: () => void;
}

export function TallyModal({ subsetId, onClose }: TallyModalProps) {
  const [activeTab, setActiveTab] = useState<'rankings' | 'special'>('rankings');
  const [tally, setTally] = useState<VoteTally[]>([]);
  const [specialCategoryTallies, setSpecialCategoryTallies] = useState<{
    [categoryIndex: number]: Array<{ songId: number; voteCount: number }>;
  }>({});
  const [subset, setSubset] = useState<SubsetWithSongs | null>(null);
  const [allSongs, setAllSongs] = useState<Map<number, SongSummary>>(new Map());
  const [userRankedSongIds, setUserRankedSongIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, [subsetId]);

  // Set up SSE connection for live updates
  useEffect(() => {
    const eventSource = new EventSource(`/api/votes/${subsetId}/live`, {
      withCredentials: true,
    });

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.regularTally) {
          setTally(data.regularTally);
          setSpecialCategoryTallies(data.specialCategoryTallies || {});
          setLastUpdated(new Date());
          setConnectionError(false);
        }
      } catch (err) {
        console.error('Failed to parse SSE data:', err);
      }
    };

    eventSource.onerror = () => {
      console.error('SSE connection error');
      eventSource.close();
      setConnectionError(true);
    };

    return () => {
      eventSource.close();
    };
  }, [subsetId]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load subset details, tally, songs, and user's vote in parallel
      const [subsetData, tallyData, songs, userVote] = await Promise.all([
        subsetsApi.getSubsetById(subsetId),
        votesApi.getTally(subsetId),
        songsApi.getAllSongs(),
        votesApi.getUserVote(subsetId).catch(() => ({ hasVoted: false })),
      ]);

      setSubset(subsetData);
      setTally(tallyData.regularTally);
      setSpecialCategoryTallies(tallyData.specialCategoryTallies || {});

      // Create songs map for efficient lookup
      const songsMap = new Map(songs.map(s => [s.id, s]));
      setAllSongs(songsMap);

      if (userVote.hasVoted && 'vote' in userVote && userVote.vote) {
        setUserRankedSongIds(userVote.vote.rankedSongIds);
      }
    } catch (err) {
      setError((err as Error).message || 'Failed to load tally');
    } finally {
      setLoading(false);
    }
  };

  // Get enabled categories for special categories tab
  const enabledCategories = useMemo(() => {
    if (!subset) return [];
    return subset.specialCategories
      .map((cat, index) => ({ ...cat, index }))
      .filter(cat => cat.enabled);
  }, [subset]);

  const hasSpecialCategories = enabledCategories.length > 0;

  // Helper to get category icon
  const getCategoryIcon = (name: string): string => {
    const lower = name.toLowerCase();
    if (lower.includes('story') || lower.includes('storytelling')) return '🎭';
    if (lower.includes('heart') || lower.includes('tugs')) return '💝';
    if (lower.includes('fun')) return '🎉';
    if (lower.includes('energy')) return '⚡';
    if (lower.includes('cover') || lower.includes('coolest')) return '🎨';
    return '🏆';
  };

  // Helper to get row styling based on rank
  const getRowClassName = (index: number): string => {
    if (index === 0) {
      // Rank #1 - Crimson monster
      return 'relative p-4 bg-gradient-to-br from-monster-crimson/30 via-monster-card to-monster-card border-2 border-monster-crimson rounded-lg';
    } else if (index === 1) {
      // Rank #2 - Blue monster
      return 'relative p-4 bg-gradient-to-br from-monster-blue/30 via-monster-card to-monster-card border-2 border-monster-blue rounded-lg';
    } else if (index === 2) {
      // Rank #3 - Purple magic
      return 'relative p-4 bg-gradient-to-br from-purple-500/30 via-monster-card to-monster-card border-2 border-purple-500 rounded-lg';
    } else {
      // Ranks #4+
      return 'relative p-3 border border-monster-border/50 rounded-lg';
    }
  };

  // Helper to get rank badge styling
  const getRankBadgeClassName = (index: number): string => {
    if (index === 0) {
      return 'relative w-14 h-14 rounded-full bg-monster-crimson flex items-center justify-center font-bold text-2xl shadow-lg shadow-monster-crimson/50';
    } else if (index === 1) {
      return 'relative w-12 h-12 rounded-full bg-monster-blue flex items-center justify-center font-bold text-xl shadow-lg shadow-monster-blue/50';
    } else if (index === 2) {
      return 'relative w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center font-bold text-xl shadow-lg shadow-purple-500/50';
    } else {
      return 'w-10 h-10 rounded-full bg-monster-dark flex items-center justify-center font-semibold text-base';
    }
  };

  // Helper to get song title text size
  const getTitleClassName = (index: number): string => {
    if (index === 0) return 'text-2xl font-bold';
    if (index === 1 || index === 2) return 'text-xl font-bold';
    return 'text-lg font-semibold';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-monster-card rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 animate-pulse text-monster-blue mx-auto mb-4" />
              <p className="text-gray-400">Loading tally...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-monster-card rounded-xl shadow-2xl w-full max-w-5xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Vote Tally</h2>
            <button
              onClick={onClose}
              className="w-12 h-12 flex items-center justify-center rounded-full hover:bg-monster-dark transition-colors"
            >
              <X size={28} />
            </button>
          </div>
          <div className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-monster-crimson mx-auto mb-4" />
            <p className="text-xl text-gray-300 mb-2">Failed to Load Tally</p>
            <p className="text-sm text-gray-500">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-monster-card rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-monster-border">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold">Vote Tally</h2>
            {/* Live indicator */}
            {!connectionError && (
              <span className="flex items-center gap-2 text-sm text-green-400">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                LIVE
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-12 h-12 flex items-center justify-center rounded-full hover:bg-monster-dark transition-colors"
          >
            <X size={28} />
          </button>
        </div>

        {/* Connection error banner */}
        {connectionError && (
          <div className="mx-4 mt-4 p-3 bg-red-600/20 border border-red-600/50 rounded flex items-start gap-2 text-sm text-red-400">
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">Connection Lost</div>
              <div className="text-xs mt-1">Live updates unavailable. Data may be outdated.</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        {hasSpecialCategories && (
          <div className="flex gap-2 px-6 pt-4 border-b border-monster-border">
            <button
              onClick={() => setActiveTab('rankings')}
              className={`px-6 py-3 rounded-t-lg font-medium transition-colors ${
                activeTab === 'rankings'
                  ? 'bg-monster-dark text-white border-t-2 border-monster-blue'
                  : 'text-gray-400 hover:text-white hover:bg-monster-dark/50'
              }`}
            >
              Rankings
            </button>
            <button
              onClick={() => setActiveTab('special')}
              className={`px-6 py-3 rounded-t-lg font-medium transition-colors ${
                activeTab === 'special'
                  ? 'bg-monster-dark text-white border-t-2 border-monster-blue'
                  : 'text-gray-400 hover:text-white hover:bg-monster-dark/50'
              }`}
            >
              Special Categories
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'rankings' && (
            <div className="space-y-3">
              {tally.length === 0 ? (
                <div className="text-center py-16">
                  <BarChart3 size={64} className="mx-auto text-gray-600 mb-4" />
                  <p className="text-xl text-gray-400">No votes yet!</p>
                  <p className="text-sm text-gray-500 mt-2">Be the first to cast your vote</p>
                </div>
              ) : (
                tally.map((item, index) => {
                  const userVoted = userRankedSongIds.includes(item.songId);
                  const userRank = userRankedSongIds.indexOf(item.songId) + 1;
                  const isTop3 = index < 3;

                  return (
                    <div key={item.songId} className={getRowClassName(index)}>
                      {/* Glow effect for top 3 */}
                      {isTop3 && (
                        <div
                          className={`absolute inset-0 rounded-lg blur-xl ${
                            index === 0
                              ? 'bg-monster-crimson/10'
                              : index === 1
                              ? 'bg-monster-blue/10'
                              : 'bg-purple-500/10'
                          }`}
                        ></div>
                      )}

                      {/* User position marker */}
                      {userVoted && (
                        <>
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500 rounded-l-lg"></div>
                          <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold shadow-lg">
                            {userRank}
                          </div>
                        </>
                      )}

                      <div className="relative flex items-center gap-4">
                        {/* Rank badge */}
                        <div className={getRankBadgeClassName(index)}>
                          #{index + 1}
                        </div>

                        {/* Song details */}
                        <div className="flex-1 min-w-0">
                          <div className={getTitleClassName(index)}>{item.title}</div>
                          <div className={`${isTop3 ? 'text-lg' : 'text-base'} text-gray-300`}>
                            {item.artist}
                          </div>
                          <div className="flex gap-4 mt-2 flex-wrap">
                            <span
                              className={`px-3 py-1 rounded-full font-bold ${
                                isTop3
                                  ? index === 0
                                    ? 'bg-monster-crimson/30 text-monster-crimson text-lg'
                                    : 'bg-monster-blue/30 text-monster-blue'
                                  : 'bg-monster-dark text-gray-300 text-sm'
                              }`}
                            >
                              {item.totalPoints} pts
                            </span>
                            <span
                              className={`px-3 py-1 rounded-full font-medium ${
                                isTop3
                                  ? 'bg-monster-blue/30 text-monster-blue'
                                  : 'bg-monster-dark text-gray-400 text-sm'
                              }`}
                            >
                              {item.voteCount} {item.voteCount === 1 ? 'vote' : 'votes'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'special' && (
            <div className="space-y-6">
              {enabledCategories.map(category => {
                const tallies = specialCategoryTallies[category.index] || [];
                const topSong = tallies[0];

                return (
                  <div key={category.index} className="border border-monster-border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-3xl">{getCategoryIcon(category.name)}</span>
                      <h3 className="text-xl font-semibold">{category.name}</h3>
                    </div>

                    {tallies.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No votes for this category yet
                      </div>
                    ) : (
                      <>
                        {/* Leader card */}
                        {topSong && (
                          <div className="p-4 bg-gradient-to-br from-monster-crimson/20 to-monster-card border border-monster-crimson/50 rounded-lg mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-monster-crimson flex items-center justify-center font-bold text-lg shadow-lg shadow-monster-crimson/50">
                                #1
                              </div>
                              <div className="flex-1">
                                <div className="text-lg font-bold">{allSongs.get(topSong.songId)?.title || 'Unknown'}</div>
                                <div className="text-sm text-gray-300">{allSongs.get(topSong.songId)?.artist || ''}</div>
                                <div className="mt-1">
                                  <span className="px-2 py-1 bg-monster-crimson/30 text-monster-crimson rounded-full text-sm font-semibold">
                                    {topSong.voteCount} {topSong.voteCount === 1 ? 'vote' : 'votes'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Rest of rankings */}
                        {tallies.slice(1, 5).map((item, index) => {
                          const song = allSongs.get(item.songId);
                          return (
                            <div
                              key={item.songId}
                              className="flex justify-between items-center p-2 border-b border-monster-border/30 last:border-b-0"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500 font-medium w-8">#{index + 2}</span>
                                <div>
                                  <div className="font-medium">{song?.title || 'Unknown'}</div>
                                  <div className="text-sm text-gray-400">{song?.artist || ''}</div>
                                </div>
                              </div>
                              <span className="text-sm text-gray-400">
                                {item.voteCount} {item.voteCount === 1 ? 'vote' : 'votes'}
                              </span>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-monster-border text-center text-sm text-gray-500">
          Last updated: {lastUpdated.toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
}
